/**
 * KAN-200 follow-up — pure detector for "the company is locked" refusals.
 * Called from the shared supabase fetch wrapper with a CLONED response, so
 * reading the body never touches the caller's. Every failure is swallowed.
 *
 * PostgREST: { code: 'CVR01'|'CVR02', message: 'COMPANY_READ_ONLY'|... }
 * Edge fns:  { code: 'COMPANY_READ_ONLY'|... } or { error: { code } }
 */
const LOCK_SQLSTATES = new Set(['CVR01', 'CVR02']);
const LOCK_WORDS = new Set(['COMPANY_READ_ONLY', 'COMPANY_SUSPENDED']);

export function isCompanyLockBody(body) {
  if (!body || typeof body !== 'object') return false;
  if (LOCK_SQLSTATES.has(body.code) || LOCK_WORDS.has(body.code)) return true;
  if (LOCK_WORDS.has(body.message)) return true;
  const nested = body.error;
  if (nested && typeof nested === 'object') {
    return LOCK_WORDS.has(nested.code) || LOCK_SQLSTATES.has(nested.code);
  }
  return false;
}

/**
 * Silent background self-heal writes the app fires on its own (no tap) — a
 * lock refusal there must never pop the dialog (QA 2026-10-15: the admin
 * dashboard's estimate repair opened it on every cold load of a locked company).
 */
export const BACKGROUND_WRITE_PATHS = Object.freeze([
  '/rest/v1/rpc/repair_invoiced_estimates_missing_invoice',
  '/rest/v1/rpc/expire_estimates_for_tenant',
]);

const isBackgroundWrite = (url) => typeof url === 'string'
  && BACKGROUND_WRITE_PATHS.some((path) => url.split('?')[0].endsWith(path));

/** @returns {Promise<boolean>} true when `response` (a clone) is a lock refusal. */
export async function detectCompanyLockResponse(url, response) {
  try {
    if (!response || response.ok) return false;
    if (typeof url === 'string' && url.includes('/auth/v1')) return false;
    if (isBackgroundWrite(url)) return false;
    const type = response.headers && response.headers.get && response.headers.get('content-type');
    if (!type || !String(type).toLowerCase().includes('json')) return false;
    return isCompanyLockBody(await response.json());
  } catch {
    return false;
  }
}

/** Fire-and-forget: clones, detects, emits. Never throws, never awaited by the caller. */
export function observeCompanyLockResponse(url, response, emit) {
  try {
    if (!response || response.ok) return;
    void detectCompanyLockResponse(url, response.clone()).then((hit) => {
      if (hit) emit();
    }).catch(() => {});
  } catch {
    /* an observer bug must never break the real fetch path */
  }
}

const isLockToken = (x) => typeof x === 'string' && LOCK_WORDS.has(x.trim());

/**
 * KAN-827 — is this a thrown/returned save ERROR (supabase-js PostgrestError, an
 * edge-function error, a bare token string) the company-lock refusal? A form
 * whose save hits one hands off to the read-only dialog instead of showing a
 * red box. The structured code is checked first (CVR01/CVR02 SQLSTATE, or the
 * edge `code` COMPANY_READ_ONLY / COMPANY_SUSPENDED); message text counts only
 * when the WHOLE message (or bare string) is the token, never as a substring of
 * an unrelated error. Every other error -> false.
 */
export function isCompanyLockError(err) {
  if (!err) return false;
  if (typeof err === 'string') return isLockToken(err);
  if (typeof err !== 'object') return false;
  if (LOCK_SQLSTATES.has(err.code) || LOCK_WORDS.has(err.code)) return true;
  if (isCompanyLockBody(err)) return true;
  return [err.message, err.error, err.context?.error].some(isLockToken)
    || (typeof err.error === 'object' && err.error !== null && isCompanyLockBody(err.error));
}
